import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import MagneticButton from "@/components/ui/MagneticButton";
import Logo from "@/components/ui/Logo";
import ThemeToggle from "@/components/ui/ThemeToggle";
import SystemStatus from "@/components/ui/SystemStatus";
import { subscribeNewsletter } from "@/lib/api";

export default function Layout({ children }) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const getLogoVariant = () => {
    if (location.pathname.includes('corporates') || location.pathname.includes('schools')) return 'corporate';
    if (location.pathname.includes('hospitals') || location.pathname.includes('science')) return 'clinical';
    return 'default';
  };

  useEffect(() => {
    const updateMousePosition = (ev) => {
      setMousePosition({ x: ev.clientX, y: ev.clientY });
    };
    window.addEventListener('mousemove', updateMousePosition);
    return () => window.removeEventListener('mousemove', updateMousePosition);
  }, []);
  
  const handleSubscribe = async (e) => {
    e.preventDefault();
    if(email) {
      await subscribeNewsletter(email);
      setSubscribed(true);
      setEmail("");
    }
  };

  const navLinks = [
    { to: "/individuals", label: "Individuals" },
    { to: "/corporates", label: "Workplaces" },
    { to: "/schools", label: "Campuses" },
    { to: "/hospitals", label: "Hospitals" },
    { to: "/science", label: "Science" },
  ];

  const NavLink = ({ to, label, mobile = false }) => {
    const isActive = location.pathname === to;
    return (
      <Link 
        to={to} 
        onClick={() => mobile && setIsMobileMenuOpen(false)}
        className={cn(
          "transition-colors duration-200 relative",
          mobile ? "text-4xl font-heading font-light py-4 block text-[var(--text-primary)]" : "text-sm font-medium py-1",
          isActive ? "text-[var(--text-primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
        )}
      >
        {label}
        {isActive && !mobile && (
          <span className="absolute -bottom-1 left-0 w-full h-px bg-[var(--nily-teal)] shadow-[0_0_8px_var(--nily-teal)]" />
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen flex flex-col font-sans text-[var(--text-primary)] relative transition-colors duration-700">
      
      <div className="pointer-events-none fixed inset-0 z-30 transition-opacity duration-300 mix-blend-screen opacity-50 dark:opacity-100" style={{ background: `radial-gradient(600px at ${mousePosition.x}px ${mousePosition.y}px, var(--glass-bg), transparent 80%)` }} />

      <SystemStatus />

      <header className="fixed top-0 left-0 right-0 z-50 px-4 md:px-6 py-4 pointer-events-none">
        <nav className="mx-auto max-w-7xl pointer-events-auto flex items-center justify-between rounded-full border border-[var(--glass-border)] bg-[var(--glass-bg)] backdrop-blur-xl px-6 py-3 shadow-lg relative z-50">
          <Link to="/" className="z-50 text-[var(--text-primary)]"><Logo variant={getLogoVariant()} /></Link>
          <div className="hidden md:flex items-center gap-8">{navLinks.map(link => <NavLink key={link.to} {...link} />)}</div>
          <div className="hidden md:flex items-center gap-4"><ThemeToggle /><Link to="/beta"><MagneticButton className="px-6 py-2 text-xs h-auto bg-[var(--nily-teal)] text-[var(--bg-void)]">Join Beta</MagneticButton></Link></div>
          <div className="md:hidden flex items-center gap-4"><ThemeToggle /><button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="pointer-events-auto text-[var(--text-primary)]">{isMobileMenuOpen ? <X /> : <Menu />}</button></div>
        </nav>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, y: -20 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, y: -20 }} 
              className="fixed inset-0 bg-[var(--bg-void)]/95 backdrop-blur-3xl z-40 flex flex-col items-center justify-center pointer-events-auto"
            >
              <div className="flex flex-col items-center gap-6">
                {navLinks.map(link => <NavLink key={link.to} {...link} mobile />)}
                <div className="pt-8 mt-4 w-full flex justify-center">
                  <Link to="/beta" onClick={() => setIsMobileMenuOpen(false)}>
                    <MagneticButton className="px-12 py-4 text-xl bg-[var(--nily-teal)] text-[var(--bg-void)]">Join Private Beta</MagneticButton>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="flex-grow pt-28 relative z-10 w-full">{children}</main>

      <footer className="border-t border-[var(--glass-border)] bg-[var(--glass-bg)] backdrop-blur-lg py-16 mt-20 relative z-10">
        <div className="mx-auto max-w-7xl px-6 grid md:grid-cols-4 gap-12 text-sm text-[var(--text-muted)]">
          <div className="space-y-6">
            <Logo variant="default" className="scale-90 origin-left" />
            <p className="leading-relaxed">Building the operating system for human health.</p>
            <div className="space-y-2">
               <p className="text-xs uppercase tracking-widest text-[var(--text-primary)] font-bold">Stay in the loop</p>
               {subscribed ? (
                  <div className="text-[var(--nily-teal)] text-sm flex items-center gap-2">✓ You are subscribed.</div>
               ) : (
                  <form onSubmit={handleSubscribe} className="flex items-center gap-2 border-b border-[var(--glass-border)] pb-2">
                     <input type="email" placeholder="email@domain.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-transparent border-none outline-none text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 w-full text-sm" required />
                     <button type="submit" className="text-[var(--text-primary)] hover:text-[var(--nily-teal)]"><ArrowRight size={16} /></button>
                  </form>
               )}
            </div>
          </div>
          <div><h4 className="font-semibold text-[var(--text-primary)] mb-4">Product</h4><ul className="space-y-3"><li><Link to="/individuals" className="hover:text-[var(--nily-teal)] transition-colors">Nily App</Link></li><li><Link to="/corporates" className="hover:text-[var(--nily-teal)] transition-colors">Nily360</Link></li><li><Link to="/hospitals" className="hover:text-[var(--nily-teal)] transition-colors">NeuraX</Link></li></ul></div>
          <div><h4 className="font-semibold text-[var(--text-primary)] mb-4">Science</h4><ul className="space-y-3"><li><Link to="/science" className="hover:text-[var(--nily-teal)] transition-colors">Neurova Engine</Link></li><li><span className="hover:text-[var(--nily-teal)] transition-colors cursor-pointer">Publications</span></li></ul></div>
          <div><h4 className="font-semibold text-[var(--text-primary)] mb-4">Legal</h4><ul className="space-y-3"><li><Link to="/privacy" className="hover:text-[var(--nily-teal)] transition-colors">Privacy</Link></li><li><Link to="/terms" className="hover:text-[var(--nily-teal)] transition-colors">Terms</Link></li></ul></div>
        </div>
      </footer>
    </div>
  );
}
